import React, { useEffect, useRef, useState } from 'react';
import './NaturezaDropdown.css';

const NATUREZA_OPTIONS = [
  { value: '', label: 'Todos materiais', className: 'natureza-todas' },
  { value: 'Gabarito', label: 'GABARITO', className: 'natureza-gabarito' },
  { value: 'Prova', label: 'PROVA', className: 'natureza-prova' },
  { value: 'Resumo', label: 'RESUMO', className: 'natureza-resumo' },
  { value: 'Outros', label: 'OUTROS', className: 'natureza-outros' }
];

function NaturezaDropdown({ value, onChange, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const optionRefs = useRef([]);

  const selectedIndex = NATUREZA_OPTIONS.findIndex((option) => option.value === value);
  const selectedOption = selectedIndex >= 0 ? NATUREZA_OPTIONS[selectedIndex] : NATUREZA_OPTIONS[0];
  const label = selectedOption.label;

  useEffect(() => {
    if (disabled && open) setOpen(false);
  }, [disabled, open]);

  useEffect(() => {
    if (!open) return;
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }, [open, selectedIndex]);

  useEffect(() => {
    if (!open) return;
    const node = optionRefs.current[activeIndex];
    if (node) node.focus();
  }, [open, activeIndex]);

  useEffect(() => {
    if (!open) return;

    const handleOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        requestAnimationFrame(() => triggerRef.current?.focus());
        return;
      }

      const focusInside = wrapperRef.current?.contains(document.activeElement);
      if (!focusInside) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex((prev) => (prev + 1) % NATUREZA_OPTIONS.length);
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex((prev) => (prev - 1 + NATUREZA_OPTIONS.length) % NATUREZA_OPTIONS.length);
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        const option = NATUREZA_OPTIONS[activeIndex];
        if (option) {
          onChange?.(option.value);
          setOpen(false);
          requestAnimationFrame(() => triggerRef.current?.focus());
        }
      }
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    document.addEventListener('keydown', handleKey);

    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, activeIndex, onChange]);

  const handleBlur = (event) => {
    if (!wrapperRef.current?.contains(event.relatedTarget)) {
      setOpen(false);
    }
  };

  const handleToggle = () => {
    if (disabled) return;
    setOpen((prev) => !prev);
  };

  const handleSelect = (option) => {
    onChange?.(option.value);
    setOpen(false);
    requestAnimationFrame(() => triggerRef.current?.focus());
  };

  return (
    <div
      className={`natureza-dropdown ${open ? 'is-open' : ''}`}
      ref={wrapperRef}
      onBlur={handleBlur}
    >
      <button
        type="button"
        className="natureza-trigger"
        onClick={handleToggle}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-disabled={disabled}
        disabled={disabled}
        ref={triggerRef}
      >
        <span className="natureza-trigger-text">{label}</span>
        <span className="natureza-trigger-sep">|</span>
        <span className="natureza-trigger-caret">▼</span>
      </button>

      {open ? (
        <div className="natureza-menu" role="listbox" aria-label="Selecionar natureza do material">
          {NATUREZA_OPTIONS.map((option, index) => (
            <button
              key={option.value || 'TODOS'}
              type="button"
              role="option"
              aria-selected={value === option.value}
              className={`natureza-option ${option.className} ${value === option.value ? 'is-selected' : ''}`}
              onClick={() => handleSelect(option)}
              ref={(el) => {
                optionRefs.current[index] = el;
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default NaturezaDropdown;
